import { useCallback, useEffect, useRef, useState } from 'react';
import { LAUNCHER } from '../components/floating/LauncherChrome';

/**
 * Hide-to-edge-tab — the USER's own dismiss for a floating launcher.
 *
 * Route suppression is the *developer* deciding a launcher must not exist on a page. This is the
 * *user* deciding — "get this bubble off my screen" — which every launcher should support,
 * especially on a phone where each floating button costs real content.
 *
 * The one non-negotiable in the pattern: **while hidden, everything un-renders EXCEPT a
 * translucent edge tab.** Rendering nothing at all would strand the user with no way back, and
 * for the navigation launcher that means stranding them with no navigation. The tab is the
 * guarantee, so it is never gated behind anything.
 *
 * The reset broadcast un-hides as well, so the footer's "↻ Reset floating panels" recovers a
 * launcher whose edge tab the user can't find.
 *
 * `justHidden` is true for `LAUNCHER.ANNOUNCE_MS` after the user hides the launcher, and drives
 * the edge tab's slide-and-pulse. It lives here rather than in each launcher so every one of them
 * announces identically — a timer hand-copied into nine components is nine chances to drift, the
 * same reason the sizes moved into LauncherChrome (2026-08-27). It is deliberately NOT set when
 * the hook restores `hidden: true` from storage on mount: a launcher you hid last week should
 * not re-announce itself every page load — only the act of hiding teaches where it went.
 *
 * vault/how-to/draggable-floating-launchers.md § Hide-to-edge-tab.
 */
export function useHideToEdge(storageKey: string, resetEvent = 'tms:reset-floating-panels') {
  const [hidden, setHiddenState] = useState<boolean>(() => {
    try { return localStorage.getItem(storageKey) === '1'; } catch { return false; }
  });
  const [justHidden, setJustHidden] = useState(false);
  const announceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const setHidden = useCallback((next: boolean) => {
    setHiddenState(next);
    try { localStorage.setItem(storageKey, next ? '1' : '0'); } catch { /* private browsing */ }
    if (announceTimer.current) clearTimeout(announceTimer.current);
    if (next) {
      setJustHidden(true);
      announceTimer.current = setTimeout(() => setJustHidden(false), LAUNCHER.ANNOUNCE_MS);
    } else {
      setJustHidden(false);
    }
  }, [storageKey]);

  /**
   * Re-read when `storageKey` CHANGES.
   *
   * The key embeds the user id, and auth resolves asynchronously — the first
   * render keys on `…_guest` and only then flips to `…_<clientId>`. `useState`'s
   * initialiser runs ONCE, on that first render, so without this a launcher the
   * user hid came back on the next page load: `setHidden` wrote under the real
   * key, and the hook only ever read the guest one. Same defect that was fixed
   * in `useDraggableFloat` (2026-08-28) — the two hooks share the pattern.
   *
   * Deliberately does NOT set `justHidden`: restoring a hidden state from
   * storage must not re-announce the edge tab on every page load. Only the act
   * of hiding teaches where it went.
   */
  useEffect(() => {
    try {
      setHiddenState(localStorage.getItem(storageKey) === '1');
    } catch { /* private browsing */ }
  }, [storageKey]);

  // Clear the announce timer on unmount — a route change mid-announce would otherwise set state
  // on an unmounted component.
  useEffect(() => () => { if (announceTimer.current) clearTimeout(announceTimer.current); }, []);

  useEffect(() => {
    const onReset = () => {
      setHiddenState(false);
      setJustHidden(false);
      if (announceTimer.current) clearTimeout(announceTimer.current);
      // Self-clearing is required, not optional: the reset dispatcher clears storage too, but a
      // listener that only changed state would let the hidden flag come back on reload.
      try { localStorage.removeItem(storageKey); } catch { /* noop */ }
    };
    window.addEventListener(resetEvent, onReset);
    return () => window.removeEventListener(resetEvent, onReset);
  }, [storageKey, resetEvent]);

  return { hidden, setHidden, justHidden };
}
